import { useImmer } from 'use-immer';
import { useState, useEffect } from 'react';
import { toastShowError } from '@orderx/lab/xr/toast';

export type UseHttpInput<T> = {
    runOnInit?: boolean;
    onResult?: (result: T) => void;
    onError?: (error) => void;
};

export interface AsyncQuery<T = any> extends State<T> {
    run(params?: any): Promise<T>;
    buttonProps: {
        onClick: any;
        isLoading: boolean;
    };
}

type PromiseCancelable = {
    isCancelled: boolean;
    promise: Promise<any>;
    cancel(): void;
};

type State<T> = {
    isInitial: boolean;
    isLoading: boolean;
    result?: T;
    promiseCancelable?: PromiseCancelable;
};

const noop = () => {};

const promiseById = {};

export function useAsync<T = any>(query: Function, props?: UseHttpInput<T>): AsyncQuery<T> {
    const [id] = useState(Math.random());
    const { runOnInit, onResult = noop, onError = noop } = props || {};

    const [state, setState] = useImmer<State<T>>({
        isInitial: true,
        isLoading: false,
        result: undefined,
        promiseCancelable: undefined,
    });

    const { promiseCancelable } = state;

    useEffect(() => {
        async function handlePromise(promiseCancelable: PromiseCancelable) {
            promiseById[id] = promiseCancelable;

            setState((state) => {
                state.isLoading = true;
            });

            let result;
            let error;

            try {
                result = await promiseCancelable.promise;
            } catch (errorResponse) {
                error = errorResponse;
            }

            const canUpdateState = !promiseCancelable.isCancelled;

            if (canUpdateState) {
                if (error) {
                    setState((state) => {
                        state.isLoading = false;
                        state.isInitial = false;
                        state.promiseCancelable = undefined;
                        promiseById[id] = undefined;
                    });

                    const message = error?.message || '';

                    if (message.includes('ER_')) {
                        toastShowError('There has been an error');
                    } else if (message.startsWith('could not find')) {
                        toastShowError('Entity not found');
                    } else if (message.endsWith(' _')) {
                        toastShowError(error.message.slice(0, -2));
                    } else {
                    }

                    onError?.(error);
                } else {
                    setState((state) => {
                        state.result = result;
                        state.isLoading = false;
                        state.isInitial = false;
                        state.promiseCancelable = undefined;
                        promiseById[id] = undefined;
                    });

                    onResult?.(result);
                }
            }
        }

        if (promiseCancelable) {
            handlePromise(promiseCancelable);

            return () => {
                promiseCancelable.cancel();
            };
        }
    }, [promiseCancelable]);

    function run(params?) {
        if (promiseById[id]) {
            return promiseById[id];
        } else {
            const promiseCancelable = makeCancelable(query(params));
            promiseById[id] = promiseCancelable;

            setState((state) => {
                state.promiseCancelable = promiseCancelable;
            });

            return promiseCancelable.promise;
        }
    }

    useEffect(() => {
        if (runOnInit) {
            run();
        }
    }, [runOnInit]);

    return {
        ...state,
        buttonProps: {
            onClick: () => run(),
            isLoading: state.isLoading,
        },
        run,
    };
}

function makeCancelable(promise): PromiseCancelable {
    let isCancelled = false;

    const wrappedPromise = new Promise((resolve, reject) => {
        promise.then(
            (result) => {
                if (!isCancelled) {
                    resolve(result);
                }
            },
            (error) => {
                if (!isCancelled) {
                    reject(error);
                }
            },
        );
    });

    return {
        promise: wrappedPromise,
        isCancelled,
        cancel() {
            promise.isCancelled = true;
            isCancelled = true;
        },
    };
}
